// Tracks one-time free-plan usage so non-premium users get exactly ONE attempt
// of each free service (mock exam, AI writing correction, reading/listening
// Probe-1 Teile, grammar pillar 1). Premium/admin users never consult this.
//
// The 'exam' flag is written when an exam is COMPLETED (logExamCompletion), and
// ONLY for free users — a premium account must never carry it. firestore.rules
// permits an examResults create whenever the flag is absent, without consulting
// subscription state at all, so flag-absence is what keeps paying customers
// writable. See mayCreateExamResult() in firestore.rules.
//
// Each flag is its own write-once doc: users/{uid}/usage/{flagId} = { usedAt }.
// Firestore rules allow the owner to CREATE a flag but never update or delete
// it, so the limits cannot be reset client-side. Flag ids:
//   exam | writingCorrection | grammar | reading_{partId} | listening_{partId}
// (The AI-correction flag is also written server-side by the
// correctwritingsubmission Cloud Function, which is the authoritative gate.)

import { db } from '../firebase';
import { doc, collection, getDocs, setDoc, serverTimestamp } from 'firebase/firestore';

const usageCollection = (userId) => collection(db, 'users', userId, 'usage');

class FreeAttemptService {
  /**
   * Flat map { [flagId]: true }, or {} when the user has none.
   *
   * Returns null when the read FAILED. Callers must not treat that as "no flags
   * used" — it previously returned {} on error, which reads as unused and
   * silently handed out an extra free attempt on every network hiccup.
   * @returns {Promise<Record<string, true>|null>}
   */
  async getFreeAttempts(userId) {
    if (!userId) return {};
    try {
      const snap = await getDocs(usageCollection(userId));
      const flags = {};
      snap.docs.forEach((d) => {
        if (d.id !== 'freeAttempts') flags[d.id] = true; // skip legacy single-doc format
      });
      return flags;
    } catch (error) {
      console.error('getFreeAttempts error:', error);
      return null; // unknown, NOT "unused"
    }
  }

  async markExamAttempt(userId) {
    return this.#mark(userId, 'exam');
  }

  async markWritingCorrection(userId) {
    return this.#mark(userId, 'writingCorrection');
  }

  /** mode: 'reading' | 'listening'; partId e.g. 'reading-part-1' */
  async markSkillAttempt(userId, mode, partId) {
    if (!mode || !partId) return false;
    return this.#mark(userId, `${mode}_${partId}`);
  }

  async markGrammarAttempt(userId) {
    return this.#mark(userId, 'grammar');
  }

  isExamUsed(attempts) {
    return !!attempts?.exam;
  }

  isWritingCorrectionUsed(attempts) {
    return !!attempts?.writingCorrection;
  }

  isSkillAttemptUsed(attempts, mode, partId) {
    return !!(partId && attempts?.[`${mode}_${partId}`]);
  }

  isGrammarUsed(attempts) {
    return !!attempts?.grammar;
  }

  async #mark(userId, flagId) {
    if (!userId || !flagId) return false;
    try {
      await setDoc(doc(usageCollection(userId), flagId), { usedAt: serverTimestamp() });
      return true;
    } catch (error) {
      // A rejected write on an existing flag just means it's already used.
      if (error?.code !== 'permission-denied') {
        console.error('markFreeAttempt error:', error);
      }
      return false;
    }
  }
}

export default new FreeAttemptService();
